<template>
  <div>
    <!-- Summary Header -->
    <div class="card mb-3">
      <div
        class="card-body d-flex justify-content-between align-items-center py-2"
      >
        <div>
          <strong>Total: {{ totalItems }} items</strong>
          <span class="text-muted ms-2">|</span>
          <strong class="ms-2">{{ totalMwh.toLocaleString() }} MWh</strong>
        </div>
        <div class="d-flex align-items-center gap-3">
          <div v-if="selectedCertificateIds.length > 0" class="text-primary">
            <strong>{{ selectedCertificateIds.length }} selected</strong>
            <span class="text-muted ms-1"
              >({{ selectedMwh.toLocaleString() }} MWh)</span
            >
          </div>
          <button
            class="btn btn-primary"
            @click="handleAssignClick"
            :disabled="selectedCertificateIds.length === 0"
          >
            <font-awesome-icon icon="user-plus" class="me-2" />
            Assign to Company
          </button>
        </div>
      </div>
    </div>

    <!-- PrimeVue DataTable -->
    <DataTable
      ref="dt"
      v-model:filters="filters"
      v-model:selection="internalSelection"
      :value="certificates"
      dataKey="id"
      :metaKeySelection="false"
      :selectAll="selectAll"
      filterDisplay="row"
      class="border rounded"
      scrollable
      scrollHeight="600px"
      :virtualScrollerOptions="virtualScrollerOptions"
      @select-all-change="onSelectAllChange"
    >
      <template #empty>
        <div class="text-center py-5">
          <div class="mb-2">
            <font-awesome-icon
              icon="inbox"
              size="2x"
              class="text-body-quaternary"
            />
          </div>
          No unassigned certificates found
        </div>
      </template>

      <!-- Selection Column -->
      <Column selectionMode="multiple" :headerStyle="{ width: '3rem' }" />

      <!-- ID -->
      <Column
        field="id"
        header="ID"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '5rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          <span class="text-body-secondary small">#{{ data.id }}</span>
        </template>
      </Column>

      <!-- Registry -->
      <Column
        field="registry_name"
        header="Registry"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '8rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          <span class="fw-semibold">{{ data.registry_name ?? "N/A" }}</span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <ColumnMultiSelectFilter
            v-model="filterModel.value"
            :options="uniqueRegistries"
            placeholder="Filter"
            activeLabel="Active"
            @filter="filterCallback()"
          />
        </template>
      </Column>

      <!-- Region -->
      <Column
        field="region"
        header="Region"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '7rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          {{ data.region ?? "N/A" }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <ColumnMultiSelectFilter
            v-model="filterModel.value"
            :options="uniqueRegions"
            placeholder="Filter"
            activeLabel="Active"
            @filter="filterCallback()"
          />
        </template>
      </Column>

      <!-- Technology -->
      <Column
        field="technology"
        header="Tech"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '8rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          <span class="badge bg-secondary-subtle text-secondary-emphasis">
            {{ data.technology ?? "N/A" }}
          </span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <ColumnMultiSelectFilter
            v-model="filterModel.value"
            :options="uniqueTechnologies"
            placeholder="Filter"
            activeLabel="Active"
            @filter="filterCallback()"
          />
        </template>
      </Column>

      <!-- Volume -->
      <Column
        field="volume_mwh"
        header="Volume"
        sortable
        dataType="numeric"
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '7rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          <span class="fw-bold text-end d-block"
            >{{ data.volume_mwh }} MWh</span
          >
        </template>
      </Column>

      <!-- Bundle Range -->
      <Column
        field="bundle_start"
        header="Bundles"
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '10rem' }"
      >
        <template #body="{ data }">
          <span
            v-if="data.bundle_start && data.bundle_end"
            class="text-body-secondary small"
          >
            {{ data.bundle_start }} - {{ data.bundle_end }}
          </span>
          <span v-else class="text-body-quaternary">-</span>
        </template>
      </Column>

      <!-- Device Name -->
      <Column
        field="production_device_name"
        header="Device Name"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '12rem' }"
        class="wrap-text"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          <span v-if="data.production_device_name">
            {{ data.production_device_name }}
          </span>
          <span v-else class="text-body-quaternary">-</span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <ColumnMultiSelectFilter
            v-model="filterModel.value"
            :options="uniqueDevices"
            searchable
            placeholder="Filter"
            activeLabel="Active"
            @filter="filterCallback()"
          />
        </template>
      </Column>

      <!-- Production Period -->
      <Column
        field="production_period_start"
        header="Production Period"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '12rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          <span class="text-body-secondary small">
            {{ formatDateTimeIntoDate(data.production_period_start) }} -
            {{ formatDateTimeIntoDate(data.production_period_end) }}
          </span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <ColumnMultiSelectFilter
            v-model="filterModel.value"
            :options="uniqueProductionMonths"
            searchable
            placeholder="Filter"
            activeLabel="Active"
            @filter="filterCallback()"
          />
        </template>
      </Column>

      <!-- Imported At -->
      <Column
        field="imported_at"
        header="Imported"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '10rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          <span class="text-body-tertiary small">
            {{ formatDateTime(data.imported_at) }}
          </span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect
            v-model="filterModel.value"
            :options="importedFilterGroups"
            optionLabel="label"
            optionValue="value"
            optionGroupLabel="label"
            optionGroupChildren="items"
            placeholder="Filter"
            class="p-column-filter w-100"
            display="chip"
            filter
            appendTo="body"
            @change="filterCallback()"
            @selectall-change="filterCallback()"
          >
            <template #filtericon>
              <font-awesome-icon
                icon="search"
                class="text-muted pv-multiselect-filter-icon"
              />
            </template>
            <template #value="{ value, placeholder }">
              <span v-if="value && value.length" class="text-primary fw-bold">
                Active
              </span>
              <span v-else>{{ placeholder }}</span>
            </template>
          </MultiSelect>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
import ColumnMultiSelectFilter from "@/components/common/ColumnMultiSelectFilter.vue";
import ColumnSortIcon from "@/components/common/ColumnSortIcon.vue";
import { FilterMatchMode, FilterService } from "@primevue/core/api";
import Column from "primevue/column";
import DataTable, { type DataTableFilterMeta } from "primevue/datatable";
import MultiSelect from "primevue/multiselect";
import { computed, nextTick, onMounted, ref, watch } from "vue";

import {
  formatDateTime,
  formatDateTimeIntoDate,
  formatDateTimeIntoIsoDate,
} from "@/composables/formatUtils";
import { isEqual, sumBy } from "lodash-es";
import type { UnknownCertificateResponse } from "./virtualPortfoliosApi";

FilterService.register(
  "productionMonth",
  (value: string | null, filterValues: string[]): boolean => {
    if (!filterValues || !filterValues.length) return true;
    if (!value) return false;
    const month = value.substring(0, 7);
    return filterValues.includes(month);
  }
);

// `imported_at` is a timestamp. Certificates arrive in import batches, so the
// filter offers both granularities: a whole calendar day, or one specific import
// time within that day. Keys are derived in local time so they match the
// timestamp shown in the column body.
// Day key: `YYYY-MM-DD`. Minute key: `YYYY-MM-DDTHH:mm`.
const importedMinuteKey = (value: string): string => {
  const date = new Date(value);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${formatDateTimeIntoIsoDate(value)}T${hours}:${minutes}`;
};

FilterService.register(
  "importedDayOrTime",
  (value: string | null, filterValues: string[]): boolean => {
    if (!filterValues || !filterValues.length) return true;
    if (!value) return false;
    const minuteKey = importedMinuteKey(value);
    const dayKey = minuteKey.slice(0, 10);
    return filterValues.some((key) => key === dayKey || key === minuteKey);
  }
);

const props = defineProps<{
  certificates: UnknownCertificateResponse[];
}>();

const emit = defineEmits<{
  "assign-click": [certificateIds: number[]];
}>();

const selectedCertificateIds = defineModel<number[]>({ required: true });

const dt = ref();
const internalSelection = ref<UnknownCertificateResponse[]>([]);

// PrimeVue re-renders the whole table body on any selection change, so mounting
// every row made each checkbox click block the main thread for ~2.8s at 1.5k
// rows (measured in prod). Virtualising keeps ~30 rows mounted regardless of
// dataset size. Mirrors PortfolioItemsTable's configuration; `autoSize` handles
// the variable row heights caused by the wrapping Device Name column.
const ITEM_HEIGHT = 42;
const VIRTUAL_SCROLLER_MIN_ROWS = 14;

const virtualScrollerOptions = computed(() =>
  props.certificates.length > VIRTUAL_SCROLLER_MIN_ROWS
    ? { itemSize: ITEM_HEIGHT, numToleratedItems: 20, autoSize: true }
    : undefined
);

watch(
  internalSelection,
  (items) => {
    const newIds = items.map((item) => item.id);
    if (!isEqual(newIds, selectedCertificateIds.value)) {
      selectedCertificateIds.value = newIds;
    }
  },
  { deep: true }
);

watch(
  [() => props.certificates, selectedCertificateIds],
  ([certs, ids]) => {
    const newSelection = certs.filter((cert) => ids.includes(cert.id));
    const currentIds = internalSelection.value.map((item) => item.id);
    const newIds = newSelection.map((item) => item.id);
    if (!isEqual(currentIds, newIds)) {
      internalSelection.value = newSelection;
    }
  },
  { immediate: true }
);

// Rows currently visible after column filters are applied. Mirrors the
// pattern used by PortfolioItemsTable: PrimeVue's DataTable exposes the
// filtered result set via `processedData`. Kept in a ref so the summary
// totals and the select-all state react to filter changes.
const filteredCertificates = ref<UnknownCertificateResponse[]>([
  ...props.certificates,
]);

const getFilteredCertificates = (): UnknownCertificateResponse[] => {
  const processedData = dt.value?.processedData;
  return Array.isArray(processedData) ? processedData : props.certificates;
};

const refreshFilteredCertificates = () => {
  filteredCertificates.value = getFilteredCertificates();
};

// Totals reflect the currently filtered rows so the header stays consistent
// with what the table displays when column filters are active.
const totalItems = computed(() => filteredCertificates.value.length);
const totalMwh = computed(() =>
  sumBy(filteredCertificates.value, "volume_mwh")
);
const selectedMwh = computed(() =>
  sumBy(internalSelection.value, "volume_mwh")
);

// Select-all is scoped to the currently filtered rows: checking adds the
// visible rows to the existing selection, unchecking removes only those.
// Selections made under other filters are preserved.
const selectAll = ref(false);

const syncSelectAll = () => {
  const filtered = filteredCertificates.value;
  const selectedIds = new Set(selectedCertificateIds.value);
  selectAll.value =
    filtered.length > 0 && filtered.every((cert) => selectedIds.has(cert.id));
};

const onSelectAllChange = (event: { checked: boolean }) => {
  const filtered = getFilteredCertificates();
  if (event.checked) {
    const merged = new Map(
      internalSelection.value.map((cert) => [cert.id, cert])
    );
    for (const cert of filtered) merged.set(cert.id, cert);
    internalSelection.value = [...merged.values()];
  } else {
    const filteredIds = new Set(filtered.map((cert) => cert.id));
    internalSelection.value = internalSelection.value.filter(
      (cert) => !filteredIds.has(cert.id)
    );
  }
  refreshFilteredCertificates();
  syncSelectAll();
};

const filters = ref<DataTableFilterMeta>({
  registry_name: { value: [], matchMode: FilterMatchMode.IN },
  region: { value: [], matchMode: FilterMatchMode.IN },
  technology: { value: [], matchMode: FilterMatchMode.IN },
  production_device_name: { value: [], matchMode: FilterMatchMode.IN },
  production_period_start: { value: [], matchMode: "productionMonth" },
  imported_at: { value: [], matchMode: "importedDayOrTime" },
});

// Recompute filtered rows and select-all state whenever the filters change.
// `processedData` is only up to date after the DataTable re-renders, so defer
// to the next tick.
watch(
  filters,
  () => {
    nextTick(() => {
      refreshFilteredCertificates();
      syncSelectAll();
    });
  },
  { deep: true }
);

// Keep filtered rows and select-all in sync when the source data or the
// selection changes (e.g. rows loaded asynchronously or selection cleared).
watch(
  [() => props.certificates, selectedCertificateIds],
  () => {
    nextTick(() => {
      refreshFilteredCertificates();
      syncSelectAll();
    });
  },
  { deep: true }
);

onMounted(() => {
  refreshFilteredCertificates();
  syncSelectAll();
});

const distinctOptions = (
  select: (cert: UnknownCertificateResponse) => string | null | undefined
) => {
  const values = new Set<string>();
  for (const cert of props.certificates) {
    const value = select(cert);
    if (value) values.add(value);
  }
  return [...values].sort().map((v) => ({ label: v, value: v }));
};

const uniqueRegistries = computed(() =>
  distinctOptions((cert) => cert.registry_name)
);
const uniqueRegions = computed(() => distinctOptions((cert) => cert.region));
const uniqueTechnologies = computed(() =>
  distinctOptions((cert) => cert.technology)
);
const uniqueDevices = computed(() =>
  distinctOptions((cert) => cert.production_device_name)
);
const uniqueProductionMonths = computed(() =>
  distinctOptions((cert) => cert.production_period_start?.substring(0, 7))
);

// One group per import day, newest first — that is the batch an admin is usually
// looking for. Each group offers "Whole day" plus every distinct import time in
// it, so the column can be filtered at either granularity.
const importedFilterGroups = computed(() => {
  const minutesByDay = new Map<string, Set<string>>();
  for (const cert of props.certificates) {
    if (!cert.imported_at) continue;
    const minuteKey = importedMinuteKey(cert.imported_at);
    const dayKey = minuteKey.slice(0, 10);
    const minutes = minutesByDay.get(dayKey) ?? new Set<string>();
    minutes.add(minuteKey);
    minutesByDay.set(dayKey, minutes);
  }

  return [...minutesByDay.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([dayKey, minuteKeys]) => {
      const [year, month, day] = dayKey.split("-");
      const dayLabel = `${day}.${month}.${year}`;
      return {
        label: dayLabel,
        items: [
          { label: `Whole day (${dayLabel})`, value: dayKey },
          ...[...minuteKeys]
            .sort()
            .reverse()
            .map((minuteKey) => ({
              label: `${dayLabel} ${minuteKey.slice(11)}`,
              value: minuteKey,
            })),
        ],
      };
    });
});

function handleAssignClick() {
  if (selectedCertificateIds.value.length > 0) {
    emit("assign-click", [...selectedCertificateIds.value]);
  }
}
</script>

<style scoped lang="scss">
.wrap-text {
  white-space: normal;
  word-break: break-word;
}
</style>
